// Browser-compatible GeminiService - WORKING VERSION
class GeminiService {
  constructor() {
    this.isInitialized = false;
    this.knowledgeStore = null;
    this.baseURL = null;
  }

  // Initialize with knowledge store reference
  initialize(knowledgeStore) {
    this.knowledgeStore = knowledgeStore;
    this.isInitialized = true;
    console.log('🤖 GeminiService initialized (Browser Mode)');
  }

  // Execute AI command via GeminiCLI backend
  async executeCommand(prompt) {
    if (!this.isInitialized) {
      throw new Error('GeminiService not initialized');
    }

    console.log('🤖 Sending to GeminiCLI backend:', prompt);
    
    let envBase = null;
    try {
      envBase = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_BACKEND_URL) || null;
    } catch (e) {
      envBase = null;
    }
    const tryBases = [];
    if (envBase) tryBases.push(envBase);
    if (this.baseURL) tryBases.push(this.baseURL);
    // Prefer production Render backend when available
    tryBases.push('https://privateapp-backend.onrender.com');
    // Dev fallbacks
    tryBases.push('http://localhost:3001', 'http://localhost:4001');

    let lastErr;
    for (const base of tryBases) {
      try {
        const response = await fetch(`${base}/api/ai`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt })
        });
        if (!response.ok) {
          lastErr = new Error(`HTTP error! status: ${response.status} @ ${base}`);
          continue;
        }
        const data = await response.json();
        this.baseURL = base; // cache working base
        console.log('✅ GeminiCLI response received from', base);
        return data.response;
      } catch (e) {
        // Network failure, try next base
        lastErr = e;
        continue;
      }
    }
    console.error('❌ GeminiCLI backend error:', lastErr);
    throw lastErr || new Error('No backend base URL responded');
  }

  // Simulate AI responses for testing
  simulateAIResponse(prompt) {
    const lower = prompt.toLowerCase();
    
    if (lower.includes('morning routines')) {
      return 'TITLE: Effective Morning Routines | CONTENT: 1. Wake up early (6 AM), 2. Drink water immediately, 3. Light exercise or stretching, 4. Meditation (10 minutes), 5. Healthy breakfast, 6. Plan your day, 7. Review goals';
    }
    
    if (lower.includes('productivity tips')) {
      return 'TITLE: Productivity Enhancement Tips | CONTENT: 1. Use time-blocking technique, 2. Eliminate distractions, 3. Take regular breaks (Pomodoro), 4. Prioritize important tasks first, 5. Set clear daily goals, 6. Use productivity tools and apps';
    }
    
    if (lower.includes('project plan')) {
      return 'NAME: Sample Project | DESCRIPTION: A well-structured project with clear objectives and timeline | TASKS: Define requirements, Create wireframes, Develop MVP, Test functionality, Launch product';
    }
    
    // Default response
    return `AI Response: I understand you want to "${prompt}". This is a simulated response for testing. In the full version, this will connect to real Gemini AI.`;
  }

  // Create AI-powered note
  async createNoteWithAI(input) {
    const topic = this.extractTopic(input, 'create a note about') || this.extractTopic(input, 'create note about');
    
    if (!topic) {
      return `❌ Please specify a topic: "Create a note about [topic]"`;
    }

    try {
      const response = await this.executeCommand(`Create a structured note about "${topic}"`);
      const parsed = this.parseAIResponse(response);
      
      if (parsed.title && parsed.content) {
        await this.knowledgeStore.addNote({
          title: parsed.title,
          content: parsed.content,
          tags: ['ai-generated', topic.toLowerCase()]
        });
        return `✅ Created note: "${parsed.title}"`;
      } else {
        // Fallback if parsing fails
        await this.knowledgeStore.addNote({
          title: `Note about ${topic}`,
          content: response,
          tags: ['ai-generated']
        });
        return `✅ Created note about ${topic}`;
      }
    } catch (error) {
      console.error('❌ Error creating AI note:', error);
      return `❌ Failed to create note: ${error.message}`;
    }
  }

  // Create AI-powered project
  async createProjectWithAI(input) {
    const projectName = this.extractTopic(input, 'help me plan project') || this.extractTopic(input, 'create project');
    
    if (!projectName) {
      return `❌ Please specify a project name: "Help me plan project [name]"`;
    }

    try {
      const response = await this.executeCommand(`Create a project plan for "${projectName}"`);
      const parsed = this.parseProjectResponse(response);
      
      await this.knowledgeStore.addProject({
        name: parsed.name || projectName,
        description: parsed.description || `AI-generated project plan for ${projectName}`,
        status: 'planning',
        priority: 'medium',
        tasks: parsed.tasks || [
          { id: 1, title: 'Define project scope', completed: false },
          { id: 2, title: 'Create timeline', completed: false },
          { id: 3, title: 'Execute plan', completed: false }
        ]
      });
      
      return `✅ Created project: "${parsed.name || projectName}"`;
    } catch (error) {
      console.error('❌ Error creating AI project:', error);
      return `❌ Failed to create project: ${error.message}`;
    }
  }

  // Create dynamic category - BULLETPROOF VERSION
  async createDynamicCategory(input) {
    console.log('🔍 Processing category creation input:', input);
    
    let categoryName = null;
    const lowerInput = input.toLowerCase().trim();
    
    // BULLETPROOF PATTERNS - Simple and reliable
    
    // Pattern 1: "Create category Travel" → Extract "Travel"
    if (lowerInput.startsWith('create category ')) {
      categoryName = input.substring(16).trim(); // Skip "create category "
    }
    
    // Pattern 2: "Create a Car category" → Extract "Car"  
    else if (lowerInput.includes('create a ') && lowerInput.includes(' category')) {
      const match = input.match(/create a (.+?) category/i);
      categoryName = match ? match[1].trim() : null;
    }
    
    // Pattern 3: "Create webroll" → Extract "Webroll"
    else if (lowerInput.includes('create webroll')) {
      categoryName = 'Webroll';
    }
    
    // Pattern 4: Quoted names "Create category 'AI Tools'" → Extract "AI Tools"
    const quotedMatch = input.match(/['"]([^'"]+)['"]/);
    if (quotedMatch) {
      categoryName = quotedMatch[1];
    }
    
    if (!categoryName || categoryName.length < 2) {
      return `❌ Please specify a category name. Examples:\n• "Create category Travel"\n• "Create a Car category"\n• "Create category 'AI Tools'"`;
    }

    console.log('✅ Extracted category name:', categoryName);

    try {
      await this.knowledgeStore.createCategory(categoryName);
      return `✅ Created category: "${categoryName}". You can now add items with "Add [item] to ${categoryName}"`;
    } catch (error) {
      console.error('❌ Error creating category:', error);
      return `❌ Failed to create category: ${error.message}`;
    }
  }

  // Add URL to any category - BULLETPROOF VERSION
  async addURLToCategory(input) {
    console.log('🔍 Processing URL addition:', input);
    
    // Extract URL
    const urlMatch = input.match(/(https?:\/\/[^\s]+)/);
    if (!urlMatch) {
      return `❌ No URL found. Please include a valid URL like https://google.com`;
    }
    const url = urlMatch[0];
    
    // Extract category name - multiple patterns
    let categoryName = null;
    const lowerInput = input.toLowerCase();
    
    // Pattern 1: "Add URL to Travel"
    const toPattern = input.match(/(?:add|put).*?to\s+([^#\s]+?)(?:\s|#|$)/i);
    if (toPattern) {
      categoryName = toPattern[1].trim();
    }
    
    // Pattern 2: "Add https://google.com Travel #search"  
    if (!categoryName) {
      const afterUrl = input.substring(input.indexOf(url) + url.length).trim();
      const firstWord = afterUrl.split(/[\s#]/)[0];
      if (firstWord && firstWord.length > 1) {
        categoryName = firstWord;
      }
    }
    
    if (!categoryName) {
      return `❌ Please specify a category. Examples:\n• "Add ${url} to Travel"\n• "Add ${url} to AI Tools"`;
    }

    // Extract tags
    const tagMatches = input.match(/#[\w-]+/g);
    const tags = tagMatches ? tagMatches.map(tag => tag.substring(1)) : [];

    console.log('✅ Extracted:', { url, categoryName, tags });

    try {
      // Get page title from URL domain
      const domain = new URL(url).hostname.replace('www.', '');
      
      await this.knowledgeStore.addItemToCategory(categoryName, {
        title: domain,
        url: url,
        type: 'url',
        tags: tags,
        description: `URL: ${url}${tags.length ? '\nTags: ' + tags.join(', ') : ''}`
      });
      
      return `✅ Added URL: ${domain} to category "${categoryName}"${tags.length ? ' with tags: ' + tags.join(', ') : ''}`;
    } catch (error) {
      console.error('❌ Error adding URL:', error);
      return `❌ Failed to add URL: ${error.message}`;
    }
  }

  // Show URLs by tag
  async showURLsByTag(input) {
    const tagMatch = input.match(/#([\[\w\]-]+)/);
    
    if (!tagMatch) {
      return `❌ No tag found. Use format: "Show me my #tagname URLs"`;
    }

    const tag = tagMatch[1];
    
    try {
      const allItems = this.knowledgeStore.allItems;
      const urlItems = allItems.filter(item => 
        item.tags && item.tags.includes(tag) && item.tags.includes('url')
      );

      if (urlItems.length === 0) {
        return `❌ No URLs found with tag #${tag}`;
      }

      const urlList = urlItems.map(item => `• ${item.url || item.title}`).join('\n');
      return `📋 Found ${urlItems.length} URLs with #${tag}:\n${urlList}`;
    } catch (error) {
      console.error('❌ Error retrieving URLs:', error);
      return `❌ Failed to retrieve URLs: ${error.message}`;
    }
  }

  // Parse and execute KB commands - ROUTER METHOD
  async handleKBCommand(input) {
    const command = input.toLowerCase().trim();
    
    console.log('🎯 Routing command:', input);

    // Note creation
    if (command.includes('create a note about') || command.includes('create note about')) {
      return await this.createNoteWithAI(input);
    }

    // Project creation
    if (command.includes('help me plan project') || command.includes('create project')) {
      return await this.createProjectWithAI(input);
    }

    // Dynamic category creation 
    if (command.includes('create category') || command.includes('create a category') || 
        command.includes('create webroll') || command.includes('create a webroll')) {
      return await this.createDynamicCategory(input);
    }

    // URL addition
    if (command.includes('add') && (command.includes('url') || command.includes('http'))) {
      return await this.addURLToCategory(input);
    }

    // Bulk URL import
    if (command.includes('bulk import') || command.includes('import urls') || 
        command.includes('import my') || command.includes('import browser tabs') ||
        command.includes('import multiple urls') || command.includes('bulk url')) {
      return await this.handleBulkImport(input);
    }

    // URL retrieval
    if (command.includes('show me my') && command.includes('url')) {
      return await this.showURLsByTag(input);
    }

    // General AI assistance - call the real GeminiCLI backend
    console.log('📡 Sending to GeminiCLI for general AI assistance');
    return await this.getAIResponse(input);
  }

  // Get AI response for general queries
  async getAIResponse(input) {
    try {
      return await this.executeCommand(input);
    } catch (error) {
      console.error('❌ AI response error:', error);
      return `❌ AI temporarily unavailable. Error: ${error.message}`;
    }
  }

  // Helper methods
  extractTopic(input, prefix) {
    const index = input.toLowerCase().indexOf(prefix.toLowerCase());
    if (index === -1) return null;
    
    // Get text after prefix
    const afterPrefix = input.substring(index + prefix.length).trim();
    
    // Extract first word/phrase (stop at common delimiters)
    const match = afterPrefix.match(/^([^,.\n]+)/);
    return match ? match[1].trim().replace(/['"]/g, '') : null;
  }

  extractQuoted(input) {
    const match = input.match(/['"]([^'"]+)['"]/);
    return match ? match[1] : null;
  }

  parseAIResponse(response) {
    const titleMatch = response.match(/TITLE:\s*([^\|]+)/i);
    const contentMatch = response.match(/CONTENT:\s*(.+)/i);
    
    return {
      title: titleMatch ? titleMatch[1].trim() : null,
      content: contentMatch ? contentMatch[1].trim() : response
    };
  }

  parseProjectResponse(response) {
    const nameMatch = response.match(/NAME:\s*([^\|]+)/i);
    const descMatch = response.match(/DESCRIPTION:\s*([^\|]+)/i);
    const tasksMatch = response.match(/TASKS:\s*(.+)/i);
    
    let tasks = [];
    if (tasksMatch) {
      tasks = tasksMatch[1].split(',').map((task, index) => ({
        id: index + 1,
        title: task.trim(),
        completed: false
      }));
    }
    
    return {
      name: nameMatch ? nameMatch[1].trim() : null,
      description: descMatch ? descMatch[1].trim() : null,
      tasks: tasks
    };
  }

  // 🎯 NEW: Handle bulk URL import
  async handleBulkImport(input) {
    console.log('📦 Handling bulk import request:', input);
    
    // Return special response that triggers bulk import UI
    return {
      type: 'bulk-import',
      message: '📦 Ready for bulk URL import! Paste your URLs below and I\'ll organize them for you.',
      action: 'show-bulk-import-form'
    };
  }
}

// Export singleton instance
export const geminiService = new GeminiService();